import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den "Schichtplan-Bericht"-Dialog
 * (`WorkShiftReportComponent`). Wird ueber den Hilfe-Button (`<app-dialog-
 * help-button>`) im Hero-Header manuell gestartet. Pattern analog zu
 * `AUTO_SUGGEST_HELP_TOUR` (siehe `auto-suggest-tutorial.config.ts`):
 * `category: 'feature-discovery'` → Guided Tour mit Spotlight + Voice-Over.
 *
 * 7 Schritte: Welcome → Format → Sprache → Berichts-Umfang →
 * Inhalte-Tab (Tab-Switch via `onBeforeShow`) → Filter-Tab (Tab-Switch) →
 * Bericht generieren (Tab-Switch zurueck zu 'format').
 */

type ReportTabId = 'format' | 'content' | 'filter';

/**
 * Wechselt programmatisch den Tab im `WorkShiftReportComponent` durch
 * synthetischen Klick auf den Tab-Button. Idempotent: prueft
 * `aria-selected`, klickt nur wenn der Ziel-Tab noch nicht aktiv ist.
 */
function switchToReportTab(tabId: ReportTabId): void {
  const tabBtn = document.querySelector<HTMLButtonElement>(`#wsr-tab-${tabId}`);
  if (tabBtn && tabBtn.getAttribute('aria-selected') !== 'true') {
    tabBtn.click();
  }
}

export const WORK_SHIFT_REPORT_HELP_TOUR: TourConfig = {
  id: TourFeature.WorkShiftReportHelp,
  version: 1,
  title: 'WORK_SHIFT_REPORT_HELP_TOUR_TITLE',
  description: 'WORK_SHIFT_REPORT_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren. Der
  // "Export/Download"-Button sitzt seit dem Toolbar-Refactor im Overflow-
  // Menue (`fc-overflowMenuButton-button`) — kein deterministischer 1-Klick-
  // Opener. Daher `route` only; User oeffnet Menue + Item selbst.
  launchContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 100,
  // 'feature-discovery' (nicht 'contextual-help'!) → V2-Renderer mit
  // Spotlight + Voice-Over statt HelpModeService mit (i)-Icons.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'WORK_SHIFT_REPORT_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight auf den Wrapper.
          anchor: AnchorId.WorkShiftReportDialog,
          title: 'TUTORIAL.WORK_SHIFT_REPORT.WELCOME.TITLE',
          content: 'TUTORIAL.WORK_SHIFT_REPORT.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Sicherstellen, dass der Format-Tab aktiv ist, falls der User aus
          // einem anderen Tab in die Tour zurueckspringt.
          onBeforeShow: () => switchToReportTab('format'),
        },
        {
          id: 'format',
          anchor: AnchorId.WorkShiftReportFormatPanel,
          title: 'TUTORIAL.WORK_SHIFT_REPORT.FORMAT.TITLE',
          content: 'TUTORIAL.WORK_SHIFT_REPORT.FORMAT.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('format'),
        },
        {
          id: 'language',
          anchor: AnchorId.WorkShiftReportLanguage,
          title: 'TUTORIAL.WORK_SHIFT_REPORT.LANGUAGE.TITLE',
          content: 'TUTORIAL.WORK_SHIFT_REPORT.LANGUAGE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('format'),
        },
        {
          id: 'scope',
          anchor: AnchorId.WorkShiftReportScope,
          title: 'TUTORIAL.WORK_SHIFT_REPORT.SCOPE.TITLE',
          content: 'TUTORIAL.WORK_SHIFT_REPORT.SCOPE.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'content',
          // Popover bleibt am kompakten Tab-Button, Spotlight umfasst den
          // ganzen Tab-Inhalt rechts. Beim Step-Enter wird der Inhalte-Tab
          // programmatisch aktiviert, damit der User sieht, was darin steckt.
          anchor: AnchorId.WorkShiftReportContentTabBtn,
          title: 'TUTORIAL.WORK_SHIFT_REPORT.CONTENT.TITLE',
          content: 'TUTORIAL.WORK_SHIFT_REPORT.CONTENT.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('content'),
        },
        {
          id: 'filter',
          anchor: AnchorId.WorkShiftReportFilterTabBtn,
          title: 'TUTORIAL.WORK_SHIFT_REPORT.FILTER.TITLE',
          content: 'TUTORIAL.WORK_SHIFT_REPORT.FILTER.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('filter'),
        },
        {
          id: 'generate',
          anchor: AnchorId.WorkShiftReportGenerateBtn,
          title: 'TUTORIAL.WORK_SHIFT_REPORT.GENERATE.TITLE',
          content: 'TUTORIAL.WORK_SHIFT_REPORT.GENERATE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Beim letzten Step zurueck zu 'format' wechseln — der Footer-Button
          // ist in jedem Tab sichtbar, aber der Format-Tab ist der natuerliche
          // Startpunkt fuer den Abschluss.
          onBeforeShow: () => switchToReportTab('format'),
          // Letzter Step — kein Zurueck-Button, sonst kann der User in den
          // Filter-Step zurueck (und damit den Tab unbeabsichtigt wechseln).
          showPrevButton: false,
        },
      ],
    },
  ],
};
